/**
 * Map System
 * Ported from OriginalGame/src/game/map.js
 * 
 * Manages all sites (locations) on the world map, NPC locations,
 * site discovery/unlocking, and player position
 */

import {
  Site,
  AdSite,
  BossSite,
  WorkSite,
  BazaarSite,
  HOME_SITE,
  AD_SITE,
  BOSS_SITE,
  WORK_SITE,
  GAS_SITE,
  BAZAAR_SITE
} from './Site'
import { emitter } from '@/common/utils/emitter'
import { useLogStore } from '@/core/store/logStore'
import { isAllItemUnlocked } from '@/common/utils/iap'
import type { SiteSaveData, MapSaveData } from '@/common/types/site.types'
import type { NPC } from '@/common/types/npc.types'
import { getString } from '@/common/utils/stringUtil'

// Note: NPCManager integration will be fully implemented in Phase 5
// For now, Map only tracks which NPCs are unlocked (npcMap)

/**
 * Map class
 * Manages world map state, sites, and NPCs
 */
export class Map {
  private npcMap: Record<number, boolean>
  private siteMap: Record<number, Site>
  private needDeleteSiteList: number[]
  public pos!: { x: number; y: number } // Initialized in init()

  constructor() {
    this.npcMap = {}
    this.siteMap = {}
    this.needDeleteSiteList = []
    // Note: pos is NOT initialized here, set in init()
  }

  /**
   * Save map state
   */
  save(): MapSaveData {
    // Convert npcMap object to array of IDs
    const npcSaveObj = Object.keys(this.npcMap).map(Number)

    // Save all sites
    const siteSaveObj: Record<string, SiteSaveData> = {}
    for (const siteId in this.siteMap) {
      const site = this.siteMap[siteId]
      siteSaveObj[String(siteId)] = site.save()
    }

    return {
      npcMap: npcSaveObj,
      siteMap: siteSaveObj,
      pos: this.pos,
      needDeleteSiteList: this.needDeleteSiteList
    }
  }

  /**
   * Restore map state from save data
   */
  restore(saveObj: MapSaveData | null): void {
    if (saveObj) {
      // Restore NPCs
      this.npcMap = {}
      for (const npcId of saveObj.npcMap) {
        this.npcMap[npcId] = true
      }

      // Restore sites
      this.siteMap = {}
      for (const siteIdStr in saveObj.siteMap) {
        const siteId = Number(siteIdStr)
        const site = this.createSite(siteId)
        site.restore(saveObj.siteMap[siteIdStr])
        this.siteMap[siteId] = site
      }

      // Restore position and deletion queue
      this.pos = saveObj.pos
      this.needDeleteSiteList = saveObj.needDeleteSiteList
    } else {
      // No save data, initialize fresh
      this.init()
    }
  }

  /**
   * Initialize map with default sites
   */
  init(): void {
    let allSites: number[]

    if (isAllItemUnlocked()) {
      // Full unlock (IAP)
      allSites = [
        1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14,
        20, 21, 22,
        30, 31, 32, 33,
        41, 42, 43,
        51, 52,
        61,
        100, 201, 202, 203, 204,
        301, 302, 303, 304, 305, 306, 307, 308, 309, 310, 311, 312,
        400,
        500, 501, 502,
        666
      ]
    } else {
      // Default sites
      allSites = [100, 201, 202, 204, 400]
    }

    // Unlock all sites
    for (const siteId of allSites) {
      this.unlockSite(siteId)
    }

    // Set home position (site 100)
    const homePos = { x: 45, y: 50 }
    const homeSite = this.getSite(HOME_SITE)
    if (homeSite) {
      homeSite.pos = homePos
      // Set player position to home
      this.pos = homeSite.pos
    } else {
      throw new Error('Home site (100) not found after initialization')
    }
  }

  /**
   * Iterate over all NPCs and sites
   * @param callback Function to call for each entity
   * @param npcManager NPCManager instance (required for NPC iteration)
   */
  forEach(callback: (entity: Site | NPC) => void, npcManager?: { getNPC: (id: number) => NPC }): void {
    // Iterate NPCs
    if (npcManager) {
      for (const npcId in this.npcMap) {
        if (this.npcMap[npcId]) {
          const npc = npcManager.getNPC(Number(npcId))
          if (npc && npc.isUnlocked) {
            callback(npc)
          }
        }
      }
    }

    // Iterate sites
    for (const siteId in this.siteMap) {
      const site = this.siteMap[siteId]
      const siteIdNum = Number(siteId)

      // Skip closed sites
      if (site.closed) continue

      // Skip boss sub-sites (300-399)
      if (siteIdNum >= 300 && siteIdNum <= 399) continue

      callback(site)
    }
  }

  /**
   * Unlock an NPC location
   * @param npcId NPC ID to unlock
   * @param npcManager NPCManager instance (optional, used to get NPC instance for events/logs)
   * 
   * Note: When called from NPCManager.unlockNpc(), the NPC is already unlocked in NPCManager.
   * This method only marks it as unlocked on the map and emits events/logs.
   */
  unlockNpc(npcId: number, npcManager?: { getNPC: (id: number) => { getName: () => string } }): void {
    // Mark NPC as unlocked in map
    this.npcMap[npcId] = true

    // Get NPC instance for events/logs if npcManager is provided
    if (npcManager) {
      const npc = npcManager.getNPC(npcId)
      
      // Emit event with NPC instance
      emitter.emit("unlock_site", npc)
      
      // Add log message
      const logStore = useLogStore.getState()
      logStore.addLog(getString(1125, npc.getName())) // Format: "The new location %s house has been unlocked."
    } else {
      // Fallback: emit event with stub data (for backward compatibility)
      emitter.emit("unlock_site", { id: npcId, type: 'npc' })
      const logStore = useLogStore.getState()
      // Get NPC name from string system
      const npcConfig = getString(`npc_${npcId}`)
      const npcName = typeof npcConfig === 'object' && npcConfig !== null && 'name' in npcConfig
        ? npcConfig.name as string
        : `NPC ${npcId}`
      logStore.addLog(getString(1125, npcName)) // Format: "The new location %s house has been unlocked."
    }
  }

  /**
   * Unlock a site
   * @param siteId Site ID to unlock
   */
  unlockSite(siteId: number): void {
    // Check if site already exists
    if (this.siteMap.hasOwnProperty(siteId)) {
      return // Site already unlocked
    }

    // Create site using factory
    const site = this.createSite(siteId)

    // Initialize site (generates rooms, etc.)
    site.init()

    // Add to map
    this.siteMap[siteId] = site

    // Emit event for UI updates (MapPanelContent listens to this)
    emitter.emit("unlock_site", site)

    // Add log message
    // TODO: Use proper string ID 1104
    const logStore = useLogStore.getState()
    logStore.addLog(getString(1104, site.getName())) // Format: "New location %s is unlocked!"
  }

  /**
   * Queue a site for deletion
   * @param siteId Site ID to close
   */
  closeSite(siteId: number): void {
    if (!this.siteMap.hasOwnProperty(siteId)) {
      return // Site doesn't exist
    }

    // Queue for deletion
    this.needDeleteSiteList.push(siteId)

    // Emit event (MapPanelContent may listen to this)
    emitter.emit("close_site", siteId)
  }

  /**
   * Process deletion queue and close sites that can be closed
   */
  deleteUnusableSite(): void {
    while (this.needDeleteSiteList.length > 0) {
      const siteId = this.needDeleteSiteList.pop()!
      const site = this.getSite(siteId)

      if (site && site.canClose()) {
        site.closed = true
      }
      // Note: If site.canClose() returns false, site remains in queue
      // This matches original behavior
    }
  }

  /**
   * Update player position on map
   * @param pos New position
   */
  updatePos(pos: { x: number; y: number }): void {
    this.pos = pos
  }

  /**
   * Get site by ID
   * @param siteId Site ID
   * @returns Site instance or undefined
   */
  getSite(siteId: number): Site | undefined {
    return this.siteMap[siteId]
  }

  /**
   * Reset player position to home
   */
  resetPos(): void {
    const homeSite = this.getSite(HOME_SITE)
    if (homeSite) {
      this.pos = homeSite.pos
    }
  }

  /**
   * Create site instance based on site type
   * @param siteId Site ID
   * @returns Site instance
   */
  private createSite(siteId: number): Site {
    if (siteId === AD_SITE) {
      return new AdSite(siteId)
    } else if (siteId === BOSS_SITE) {
      return new BossSite(siteId)
    } else if (siteId === WORK_SITE || siteId === GAS_SITE) {
      return new WorkSite(siteId)
    } else if (siteId === BAZAAR_SITE) {
      return new BazaarSite(siteId)
    } else {
      return new Site(siteId)
    }
  }
}

